import React, { useContext, useEffect, useState } from "react";
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useColorScheme,
  View,
  Image,
} from "react-native";
import { AntDesign, Feather, MaterialIcons } from "@expo/vector-icons";
import { useIsFocused } from "@react-navigation/native";
import { useToast } from "react-native-toast-notifications";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import UpdateConfirmationScreen from "../../Operations/partials/UpdateConfirmationScreen";
import {
  deleteOrganizationsById,
  getFileFromS3,
  getOrganizationsById,
} from "../../../services/adminService";

interface Address {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface ContactInfo {
  email: string;
  phone: string;
}

interface Organization {
  _id: string;
  name: string;
  description: string;
  logo: string;
  industry: string;
  size: string;
  website: string;
  address: Address;
  contactInfo: ContactInfo;
  owners: string[];
  admins: string[];
  members: string[];
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface Props {
  navigation: any;
  route: any;
}

const AdminOrganizationalNavigate: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const toast = useToast();
  const { isMediumDevice, isMobileDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );
  const isFocused = useIsFocused();
  const [title, setTitle] = useState("");
  const [action, setAction] = useState("");
  const [confirmationTextCS, setConfirmationTextCS] = useState("");
  const [successfulTextCS, setSuccessfulTextCS] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [approved, setApproved] = useState(false);
  let { mode, companyId } = route.params;
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const colorScheme = useColorScheme();
  const [logo, setLogo] = useState<string>("");

  useEffect(() => {
    if (mode == undefined || companyId == undefined) {
      setTimeout(() => {
        navigation.reset({
          index: 0,
          routes: [
            {
              name: "AdminHome",
              params: { screen: "AdminOrganizations" },
            },
          ],
        });
      }, 1500);
    } else {
      const fetchOrganization = async () => {
        try {
          const data = await getOrganizationsById(companyId);
          setSelectedOrganization(data);

          if (data.logo && data.logo !== "") {
            const fileData: any = await getFileFromS3(data.logo, "company");
            if (fileData?.imageUrl) {
              setLogo(fileData.imageUrl);
            }
          }
        } catch (error: any) {
          toast.show(error?.message || "Failed to load organization");
        }
      };

      fetchOrganization();
    }
  }, [mode, companyId]);

  useEffect(() => {
    if (isFocused) {
      switch (mode) {
        case "edit":
          setTitle("View Organization");
          setApproved(true);
          break;
        case "delete":
          setTitle("Delete Organization");
          setApproved(false);
          break;
        default:
          break;
      }
      setAction(mode);
      setLoaded(true);
    } else setLoaded(false);
  }, [isFocused]);

  useEffect(() => {
    // Handle title and confirmation text based on action
    switch (action) {
      case "edit":
        setTitle("View Organization");
        break;
      case "delete":
        setTitle("Delete Organization");
        setConfirmationTextCS(
          "Are you sure you want to delete this organization?"
        );
        break;
      default:
        setTitle("");
        setConfirmationTextCS("");
        break;
    }
  }, [action]);

  useEffect(() => {
    // Directly call deleteOperation when approved and action is delete
    if (approved && action === "delete") {
      // Validate selected organization before attempting to delete
      if (selectedOrganization && selectedOrganization._id) {
        deleteDirectOperation(selectedOrganization._id);
      } else {
        console.error("No organization selected for deletion.");
      }
    }
  }, [approved, action]);

  const approveCS = () => {
    setApproved(true);
  };

  const rejectCS = () => {
    navigation.reset({
      index: 0,
      routes: [{ name: "AdminHome", params: { screen: "AdminOrganizations" } }],
    });
  };

  const updateOperation = () => {
    navigation.navigate("UpdateAdminOrganizations", {
      companyId: companyId,
    });
  };

  const deleteDirectOperation = async (id: string) => {
    await deleteOrganizationsById(id)
      .then((result: any) => {
        toast.show(result?.data.message, {
          type: "success",
        });
        navigation.reset({
          index: 0,
          routes: [
            {
              name: "AdminHome",
              params: { screen: "AdminOrganizations" },
            },
          ],
        });
      })
      .catch((err) => {
        toast.show(err.message, {
          type: "danger",
        });
      });
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const capitalizeFirstLetter = (string: string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return (
    <>
      {approved ? (
        <ScrollView
          style={{
            height: "100%",
            backgroundColor: colorScheme === "light" ? "#fff" : "#fff",
          }}
        >
          {/* Organization Header Section */}
          <View style={styles.header}>
            <View style={styles.headerNav}>
              <TouchableOpacity
                onPress={() => navigation.navigate("AdminOrganizations")}
                style={styles.backButton}
              >
                <AntDesign name="arrowleft" size={24} color="white" />
              </TouchableOpacity>
              <Text style={styles.headerText}>
                {selectedOrganization?.name || "Organization Details"}
              </Text>
              {action === "edit" && (
                <TouchableOpacity
                  onPress={updateOperation}
                  style={styles.editButton}
                >
                  <Feather name="edit" size={20} color="white" />
                </TouchableOpacity>
              )}
            </View>
            {logo && logo != "" && (
              <View style={styles.logoContainer}>
                <Image
                  source={{ uri: logo }}
                  style={styles.logo}
                  resizeMode="contain"
                />
              </View>
            )}
          </View>

          {/* Organization Basic Info Section */}
          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <Text style={styles.cardTitle}>Basic Information</Text>
              {action === "edit" && (
                <TouchableOpacity onPress={updateOperation}>
                  <Feather name="edit" size={20} color="#007BFF" />
                </TouchableOpacity>
              )}
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Industry:</Text>
              <Text style={styles.value}>
                {selectedOrganization?.industry || "N/A"}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Size:</Text>
              <Text style={styles.value}>
                {capitalizeFirstLetter(selectedOrganization?.size || "N/A")}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Status:</Text>
              <View style={styles.statusContainer}>
                <Text
                  style={[
                    styles.statusText,
                    {
                      color:
                        selectedOrganization?.status === "active"
                          ? "#28a745"
                          : "#dc3545",
                    },
                  ]}
                >
                  {capitalizeFirstLetter(selectedOrganization?.status || "N/A")}
                </Text>
              </View>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Website:</Text>
              <Text style={styles.linkValue}>
                {selectedOrganization?.website || "N/A"}
              </Text>
            </View>
          </View>

          {/* Description Section */}
          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <Text style={styles.cardTitle}>Description</Text>
            </View>
            <Text style={styles.descriptionText}>
              {selectedOrganization?.description || "No description available."}
            </Text>
          </View>

          {/* Contact Information Section */}
          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <Text style={styles.cardTitle}>Contact Information</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Email:</Text>
              <Text style={styles.value}>
                {selectedOrganization?.contactInfo?.email || "N/A"}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Phone:</Text>
              <Text style={styles.value}>
                {selectedOrganization?.contactInfo?.phone || "N/A"}
              </Text>
            </View>
          </View>

          {/* Address Section */}
          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <Text style={styles.cardTitle}>Address</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Street:</Text>
              <Text style={styles.value}>
                {selectedOrganization?.address?.street || "N/A"}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>City:</Text>
              <Text style={styles.value}>
                {selectedOrganization?.address?.city || "N/A"}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>State:</Text>
              <Text style={styles.value}>
                {selectedOrganization?.address?.state || "N/A"}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Zip Code:</Text>
              <Text style={styles.value}>
                {selectedOrganization?.address?.zipCode || "N/A"}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Country:</Text>
              <Text style={styles.value}>
                {selectedOrganization?.address?.country || "N/A"}
              </Text>
            </View>
          </View>

          {/* Users Section */}
          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <Text style={styles.cardTitle}>User Information</Text>
            </View>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Owners</Text>
                <Text style={styles.countBadge}>
                  {selectedOrganization?.owners?.length || 0}
                </Text>
              </View>
              {/* Consider adding a user list view component here */}
            </View>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Admins</Text>
                <Text style={styles.countBadge}>
                  {selectedOrganization?.admins?.length || 0}
                </Text>
              </View>
              {/* Consider adding a user list view component here */}
            </View>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Members</Text>
                <Text style={styles.countBadge}>
                  {selectedOrganization?.members?.length || 0}
                </Text>
              </View>
              {/* Consider adding a user list view component here */}
            </View>
          </View>

          {/* Timestamps Section */}
          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <Text style={styles.cardTitle}>Timestamps</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Created:</Text>
              <Text style={styles.value}>
                {formatDate(selectedOrganization?.createdAt || "")}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Last Updated:</Text>
              <Text style={styles.value}>
                {formatDate(selectedOrganization?.updatedAt || "")}
              </Text>
            </View>
          </View>
        </ScrollView>
      ) : (
        <UpdateConfirmationScreen
          confirmationText={confirmationTextCS}
          successfulText={successfulTextCS}
          approve={approveCS}
          reject={rejectCS}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  header: {
    backgroundColor: "#007BFF",
    borderRadius: 0,
    padding: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 3,
  },
  headerNav: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  backButton: {
    padding: 5,
  },
  editButton: {
    padding: 5,
  },
  headerText: {
    color: "#FFF",
    fontSize: 20,
    fontWeight: "bold",
    textAlign: "center",
    flex: 1,
  },
  logoContainer: {
    alignItems: "center",
    marginTop: 10,
  },
  logo: {
    width: 100,
    height: 100,
    borderRadius: 10,
    backgroundColor: "#fff",
  },
  card: {
    backgroundColor: "#FFF",
    borderRadius: 8,
    padding: 16,
    marginHorizontal: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#333",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
    alignItems: "center",
  },
  label: {
    fontSize: 16,
    color: "#555",
    fontWeight: "600",
  },
  value: {
    fontSize: 16,
    color: "#333",
    maxWidth: "60%",
    textAlign: "right",
  },
  linkValue: {
    fontSize: 16,
    color: "#007BFF",
    textDecorationLine: "underline",
    maxWidth: "60%",
    textAlign: "right",
  },
  descriptionText: {
    fontSize: 16,
    color: "#333",
    lineHeight: 24,
  },
  statusContainer: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: "#f8f8f8",
  },
  statusText: {
    fontWeight: "bold",
    fontSize: 14,
  },
  section: {
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#555",
  },
  countBadge: {
    marginLeft: 8,
    backgroundColor: "#007BFF",
    color: "#fff",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    fontSize: 12,
    fontWeight: "bold",
  },
});

export default AdminOrganizationalNavigate;
